import { useLocation } from 'react-router-dom';
import * as React from 'react';
import { pageview } from '../../../shared/analytics/gtag';
import { Badge, BadgeProps, DescItem } from '@setel/portal-ui';
import { AxiosError } from 'axios';
import tw from 'twin.macro';
import { useIndexBadges } from '../badge.queries';
import { UserBadgeStatus } from '../../../shared/interfaces/badges.interface';

type BadgeListingProps = {
  userId: string;
};

const COLOR_BY_STATUS: Record<UserBadgeStatus, BadgeProps['color']> = {
  LOCKED: 'grey',
  UNLOCKED: 'success',
  IN_PROGRESS: 'grey',
};

export function CustomDescList({ children }: { children: React.ReactNode }) {
  return (
    <div
      css={tw`py-5`}
      // since this app is dealing with 320px screen size. It makes more sense to have a consistence interface
      style={{ gridTemplateColumns: '130px 155px', display: 'inline-grid' }}
    >
      {children}
    </div>
  );
}

export function BadgeListing({ userId }: BadgeListingProps) {
  let location = useLocation();

  React.useEffect(() => {
    pageview(location.pathname, 'Badge');
  }, [location]);

  const {
    data: list,
    isError,
    isLoading,
  } = useIndexBadges(
    userId,
    'UNLOCKED',
    { perPage: 5, page: 1 },
    {
      retry: (retryCount, err) => {
        const error = err as AxiosError;
        return retryCount < 3 && error?.response?.status !== 404;
      },
    }
  );

  return (
    <div className="px-4">
      <div className="text-xs uppercase font-bold text-lightgrey">Badges</div>
      <div
        css={[
          tw`overflow-y-auto overflow-x-hidden relative`,
          'max-height:283px',
        ]}
        data-react-setel-scrollbar
        data-testId="badges-listing"
      >
        {!isLoading && !!list?.items?.length && (
          <CustomDescList>
            {list.items.map((item, index) => (
              <DescItem
                valueClassName={index ? 'mt-4' : ''}
                labelClassName={`pr-2 ${index ? 'mt-4' : ''}`}
                label={item.content?.title}
                value={
                  <Badge
                    className="tracking-wider font-semibold"
                    rounded="rounded"
                    color={COLOR_BY_STATUS[item.status]}
                  >
                    {item.status}
                  </Badge>
                }
              />
            ))}
          </CustomDescList>
        )}

        {!isLoading && (!list || isError || !list.items.length) && (
          <div className="py-5" data-testid="badges-listing-empty">
            <p className="text-center text-gray-400 text-sm">
              No badge unlocked yet
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
